// The sound engine: one AudioContext, created on the first press or key (a
// browser starts audio only after a user gesture), a master gain at the
// viewer's volume, and a short room reverb that rounds the synthesized voices.
// A recording plays dry: it carries its own room. A sound never delays or
// replaces the toast it accompanies: it is fire-and-forget.
import { type FileSoundOption, type SoundOption, isFileOption, soundOption } from './catalog'
import { newSoundPolicyState, shouldPlaySound } from './policy'
import { SOUND_EVENTS, type SoundEvent, readSoundSettings } from './settings'
import { SYNTH_VOICES } from './voices'

const REVERB_SECONDS = 0.9
const REVERB_WET = 0.16
/** A recording is scaled so its loudest sample sits here: files cut at
 *  different levels play at one level, near the synthesized voices'. */
const FILE_PEAK = 0.5
/** A trimmed recording's tail fades over this, so a cut bell rings out
 *  instead of stopping; an untrimmed one ends on its own (10 ms guard). */
const TRIM_FADE = 0.15
const END_FADE = 0.01
/** A file that took longer than this to arrive is not played late. */
const FILE_LATE_MS = 1_000

interface Graph {
  ctx: AudioContext
  master: GainNode
  input: GainNode
}

let graph: Graph | null = null
const policy = newSoundPolicyState()

function roomImpulse(ctx: BaseAudioContext): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * REVERB_SECONDS)
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate)
  for (let channel = 0; channel < 2; channel++) {
    const data = buffer.getChannelData(channel)
    for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3
  }
  return buffer
}

function ensureGraph(): Graph | null {
  if (graph) return graph
  if (typeof window === 'undefined') return null
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  const ctx = new Ctor()
  const master = ctx.createGain()
  master.connect(ctx.destination)
  const input = ctx.createGain()
  const reverb = ctx.createConvolver()
  reverb.buffer = roomImpulse(ctx)
  const wet = ctx.createGain()
  wet.gain.value = REVERB_WET
  input.connect(master)
  input.connect(reverb).connect(wet).connect(master)
  graph = { ctx, master, input }
  return graph
}

interface LoadedFile {
  buffer: AudioBuffer
  /** gain that brings the file's peak to FILE_PEAK */
  scale: number
}

const files = new Map<string, Promise<LoadedFile>>()

function peakOf(buffer: AudioBuffer): number {
  let peak = 0
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const data = buffer.getChannelData(channel)
    for (let i = 0; i < data.length; i++) {
      const v = Math.abs(data[i] ?? 0)
      if (v > peak) peak = v
    }
  }
  return peak
}

function loadFile(ctx: BaseAudioContext, url: string): Promise<LoadedFile> {
  let loaded = files.get(url)
  if (!loaded) {
    loaded = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`sound ${url}: ${response.status}`)
        return response.arrayBuffer()
      })
      .then((bytes) => ctx.decodeAudioData(bytes))
      .then((buffer) => {
        const peak = peakOf(buffer)
        return { buffer, scale: peak > 0 ? FILE_PEAK / peak : 1 }
      })
    // a failed fetch is tried again next time, not remembered
    loaded.catch(() => files.delete(url))
    files.set(url, loaded)
  }
  return loaded
}

function playFile(g: Graph, option: FileSoundOption, asked: number): void {
  void loadFile(g.ctx, option.file)
    .then(({ buffer, scale }) => {
      if (Date.now() - asked > FILE_LATE_MS || g.ctx.state !== 'running') return
      const start = Math.min(option.start ?? 0, buffer.duration)
      const length = Math.min(option.length ?? buffer.duration - start, buffer.duration - start)
      if (!(length > 0)) return
      const t = g.ctx.currentTime + 0.005
      const src = g.ctx.createBufferSource()
      src.buffer = buffer
      const env = g.ctx.createGain()
      env.gain.setValueAtTime(scale, t)
      const fade = Math.min(option.length ? TRIM_FADE : END_FADE, length / 3)
      env.gain.setValueAtTime(scale, t + length - fade)
      env.gain.linearRampToValueAtTime(0, t + length)
      src.connect(env).connect(g.master)
      src.start(t, start, length)
    })
    .catch(() => {
      // audio is a garnish: a missing file never breaks the action it marks
    })
}

/** Fetches and decodes the files the viewer's events play, ahead of them. */
function preloadDefaults(g: Graph): void {
  for (const event of SOUND_EVENTS) {
    const option = soundOption(event)
    if (isFileOption(option)) void loadFile(g.ctx, option.file).catch(() => undefined)
  }
}

/** Starts (or resumes) audio. Safe to call on every gesture. */
let preloaded = false

export function unlockSound(): void {
  const g = ensureGraph()
  if (g && g.ctx.state === 'suspended') void g.ctx.resume().catch(() => undefined)
  // once, on the first gesture, whoever created the graph (a WS sound before
  // any press builds it suspended)
  if (g && !preloaded) {
    preloaded = true
    preloadDefaults(g)
  }
}

let unlockInstalled = false

/** Arms audio on the first press or key anywhere in the app. */
export function installSoundUnlock(): void {
  if (unlockInstalled || typeof window === 'undefined') return
  unlockInstalled = true
  const unlock = () => {
    unlockSound()
    if (graph?.ctx.state === 'running') {
      window.removeEventListener('pointerdown', unlock, true)
      window.removeEventListener('keydown', unlock, true)
    }
  }
  window.addEventListener('pointerdown', unlock, true)
  window.addEventListener('keydown', unlock, true)
}

function render(option: SoundOption, volume: number): void {
  const g = ensureGraph()
  if (!g || g.ctx.state !== 'running') return
  g.master.gain.setValueAtTime(volume, g.ctx.currentTime)
  if (isFileOption(option)) {
    playFile(g, option, Date.now())
    return
  }
  SYNTH_VOICES[option.synth](g.ctx, g.input, g.ctx.currentTime + 0.005)
}

/**
 * Plays an event's sound if the viewer's settings and the policy allow it.
 * `key` names the fact (an attempt, an order, an alert): one fact sounds once.
 */
export function playSound(event: SoundEvent, key?: string): void {
  // MONEY PATH callers (a trade's confirm, a transfer's send): nothing here
  // may throw into them, so the whole body is guarded, not only the device
  try {
    const settings = readSoundSettings()
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
    if (!shouldPlaySound(policy, settings, event, key, Date.now(), hidden)) return
    render(soundOption(event), settings.volume)
  } catch {
    // audio is a garnish: a failing device never breaks the action it marks
  }
}

/** Plays a sound on request (settings, the listening page): no policy, no mute. */
export function previewSound(event: SoundEvent, optionId?: string): void {
  unlockSound()
  const option = soundOption(event, optionId)
  const volume = readSoundSettings().volume || 0.6
  const g = graph
  if (g && g.ctx.state !== 'running') {
    void g.ctx
      .resume()
      .then(() => render(option, volume))
      .catch(() => undefined)
    return
  }
  try {
    render(option, volume)
  } catch {
    // see playSound
  }
}
